import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Plus, AlertCircle, Crown, Trash2, Ban, EyeOff, Eye, Unlock } from 'lucide-react';
import api from '../api';
import { TIPI, tipoLabel } from '../utils/tipi';
import ConfirmModal from '../components/ConfirmModal';
import './CreaServizio.css';

const STATO = {
  in_attesa: { label: 'in attesa', cls: 'warn' },
  approvata: { label: 'approvata', cls: 'ok' },
  rifiutata: { label: 'rifiutata', cls: 'off' },
};

// Dettaglio admin di un fornitore: profilo + tutte le sue vetrine (schede),
// con moderazione e creazione scheda per suo conto.
function FornitoreDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fornitore, setFornitore] = useState(null);
  const [schede, setSchede] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creaOpen, setCreaOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const alertModal = (message, title = 'Errore') =>
    setModal({ title, message, confirmLabel: 'Ho capito', cancelLabel: null, onConfirm: () => setModal(null) });

  const load = useCallback(async () => {
    setError(null);
    try {
      const [fRes, sRes] = await Promise.all([
        api.get(`/admin/fornitori/${id}`),
        api.get('/admin/schede', { params: { provider_id: id } }),
      ]);
      setFornitore(fRes.data.fornitore || fRes.data);
      setSchede(sRes.data.schede || []);
    } catch (err) {
      setError(err.response?.data?.errore || 'Errore nel caricamento del fornitore');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const eseguiEliminaFornitore = async () => {
    setModal(null);
    try {
      await api.delete(`/admin/fornitori/${fornitore.id}`);
      navigate('/admin');
    } catch (err) {
      alertModal(err.response?.data?.errore || 'Errore nell\'eliminazione del fornitore');
    }
  };
  const eliminaFornitore = () => {
    const nome = fornitore.nome_attivita || `Fornitore #${fornitore.id}`;
    const n = schede.length;
    const avviso = n > 0
      ? `Eliminare "${nome}"? Verranno eliminate anche le sue ${n} vetrine, le foto e i messaggi. L'azione è irreversibile.`
      : `Eliminare "${nome}"? L'azione è irreversibile.`;
    setModal({
      title: 'Eliminare il fornitore?',
      message: avviso, variant: 'danger', confirmLabel: 'Elimina', cancelLabel: 'Annulla',
      onConfirm: eseguiEliminaFornitore,
    });
  };

  const eseguiCambiaStato = async (patch) => {
    setModal(null);
    try {
      await api.patch(`/admin/fornitori/${fornitore.id}/stato`, patch);
      load();
    } catch (err) {
      alertModal(err.response?.data?.errore || 'Errore nell\'aggiornare lo stato');
    }
  };
  const cambiaStato = (patch, conferma) => {
    if (conferma) {
      setModal({
        title: 'Confermi?', message: conferma,
        confirmLabel: 'Conferma', cancelLabel: 'Annulla',
        onConfirm: () => eseguiCambiaStato(patch),
      });
    } else {
      eseguiCambiaStato(patch);
    }
  };

  const moderaScheda = async (scheda, stato) => {
    let motivo;
    if (stato === 'rifiutata') {
      motivo = window.prompt('Motivo del rifiuto (opzionale, sara\' mostrato al fornitore):', scheda.motivo_rifiuto || '');
      if (motivo === null) return; // annullato
    }
    try {
      await api.patch(`/admin/schede/${scheda.id}/approvazione`, { stato, motivo: motivo || undefined });
      load();
    } catch (err) {
      alertModal(err.response?.data?.errore || 'Errore nella moderazione');
    }
  };

  // Mette in pausa o riattiva una vetrina (l'admin può farlo su qualsiasi scheda).
  const cambiaAttivo = async (scheda) => {
    try {
      await api.patch(`/servizi/${scheda.id}/stato`, { attivo: !scheda.attivo });
      load();
    } catch (err) {
      alertModal(err.response?.data?.errore || 'Errore nel cambiare lo stato della vetrina');
    }
  };

  const eseguiEliminaScheda = async (scheda) => {
    setModal(null);
    try {
      await api.delete(`/admin/schede/${scheda.id}`);
      load();
    } catch (err) {
      alertModal(err.response?.data?.errore || 'Errore nell\'eliminazione della vetrina');
    }
  };
  const eliminaScheda = (scheda) => {
    setModal({
      title: 'Eliminare la vetrina?',
      message: `Eliminare "${scheda.nome}"? Verranno eliminati anche foto e listino. L'azione è irreversibile.`,
      variant: 'danger', confirmLabel: 'Elimina', cancelLabel: 'Annulla',
      onConfirm: () => eseguiEliminaScheda(scheda),
    });
  };

  if (loading) {
    return <div className="admin-page"><p>Caricamento...</p></div>;
  }
  if (error && !fornitore) {
    return (
      <div className="admin-page">
        <div className="error-message"><AlertCircle size={16} /> {error}</div>
        <p><Link to="/admin">← Torna al pannello</Link></p>
      </div>
    );
  }

  const isPro = fornitore.piano === 'pro';

  return (
    <div className="admin-page">
      <button className="btn btn-secondary btn-back" onClick={() => navigate('/admin')}>
        <ArrowLeft size={16} /> Pannello
      </button>

      <div className="section-head">
        <h1>{fornitore.nome_attivita || `Fornitore #${fornitore.id}`}</h1>
        <div className="fornitore-azioni">
          {fornitore.vetrine_sospese ? (
            <button className="btn btn-secondary btn-mini-cta" onClick={() => cambiaStato({ vetrine_sospese: false })}>
              <Eye size={16} /> Riattiva vetrine
            </button>
          ) : (
            <button className="btn btn-outline btn-mini-cta" onClick={() => cambiaStato({ vetrine_sospese: true }, 'Sospendere le vetrine di questo fornitore? Spariranno dal sito pubblico (reversibile).')}>
              <EyeOff size={16} /> Sospendi vetrine
            </button>
          )}
          {fornitore.bloccato ? (
            <button className="btn btn-secondary btn-mini-cta" onClick={() => cambiaStato({ bloccato: false })}>
              <Unlock size={16} /> Sblocca account
            </button>
          ) : (
            <button className="btn btn-danger btn-mini-cta" onClick={() => cambiaStato({ bloccato: true }, 'Bloccare questo account? Non potrà più usare la piattaforma e le sue vetrine saranno nascoste (reversibile).')}>
              <Ban size={16} /> Blocca account
            </button>
          )}
          <button className="btn btn-danger btn-mini-cta" onClick={eliminaFornitore}>
            <Trash2 size={16} /> Elimina
          </button>
        </div>
      </div>

      {/* Scheda anagrafica del fornitore */}
      <div className="admin-section">
        <div className="fornitore-meta">
          <span className={`badge-pill ${isPro ? 'ok' : 'off'}`}>
            {isPro ? <><Crown size={12} /> Pro</> : 'Free'}
          </span>
          {fornitore.placeholder && <span className="badge-pill warn">senza account</span>}
          {fornitore.bloccato && <span className="badge-pill off">bloccato</span>}
          {fornitore.vetrine_sospese && <span className="badge-pill warn">vetrine sospese</span>}
          {fornitore.tipo_provider && <span className="badge-pill">{tipoLabel(fornitore.tipo_provider)}</span>}
          {fornitore.citta && <span className="meta-item">{fornitore.citta}{fornitore.regione ? `, ${fornitore.regione}` : ''}</span>}
          {fornitore.email && <span className="meta-item">{fornitore.email}</span>}
          {fornitore.telefono && <span className="meta-item">{fornitore.telefono}</span>}
        </div>
        <div className="fornitore-counters">
          <div className="admin-stat"><div className="num">{fornitore.schede_totali ?? schede.length}</div><div className="lbl">Vetrine totali</div></div>
          <div className="admin-stat"><div className="num">{fornitore.schede_attive ?? schede.filter(s => s.attivo).length}</div><div className="lbl">Attive</div></div>
          <div className="admin-stat"><div className="num">{fornitore.messaggi_totali ?? 0}</div><div className="lbl">Messaggi</div></div>
        </div>
        {fornitore.descrizione_attivita && <p className="fornitore-descrizione">{fornitore.descrizione_attivita}</p>}
      </div>

      {/* Vetrine (schede) del fornitore */}
      <div className="admin-section">
        <div className="section-head">
          <h2>Vetrine ({schede.length})</h2>
          <button className="btn btn-primary btn-mini-cta" onClick={() => setCreaOpen((v) => !v)}>
            <Plus size={16} /> Aggiungi vetrina
          </button>
        </div>

        {creaOpen && (
          <CreaSchedaPerFornitore
            providerId={fornitore.id}
            onCreata={() => { setCreaOpen(false); load(); }}
          />
        )}

        {schede.length === 0 ? (
          <p className="muted">Questo fornitore non ha ancora vetrine.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr><th>Nome</th><th>Tipo</th><th>Stato</th><th>Attiva</th><th>Foto</th><th>Azioni</th></tr>
            </thead>
            <tbody>
              {schede.map((s) => {
                const st = STATO[s.stato_approvazione] || { label: s.stato_approvazione, cls: '' };
                return (
                  <tr key={s.id}>
                    <td><Link to={`/servizio/${s.id}`} target="_blank" rel="noreferrer">{s.nome} <ExternalLink size={12} /></Link></td>
                    <td>{tipoLabel(s.tipo)}</td>
                    <td><span className={`badge-pill ${st.cls}`}>{st.label}</span></td>
                    <td>{s.attivo ? <span className="badge-pill ok">attiva</span> : <span className="badge-pill off">off</span>}</td>
                    <td>{s.numero_foto ?? 0}</td>
                    <td className="admin-actions">
                      {s.stato_approvazione !== 'approvata' && (
                        <button className="btn-mini approva" onClick={() => moderaScheda(s, 'approvata')}>Approva</button>
                      )}
                      {s.stato_approvazione !== 'rifiutata' && (
                        <button className="btn-mini rifiuta" onClick={() => moderaScheda(s, 'rifiutata')}>Rifiuta</button>
                      )}
                      <button className="btn-mini" onClick={() => cambiaAttivo(s)}>{s.attivo ? 'Pausa' : 'Riattiva'}</button>
                      <Link className="btn-mini" to={`/dashboard/servizio/${s.id}/modifica`}>Modifica</Link>
                      <button className="btn-mini rifiuta" onClick={() => eliminaScheda(s)}>Elimina</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmModal
        open={!!modal}
        title={modal?.title}
        message={modal?.message}
        variant={modal?.variant}
        confirmLabel={modal?.confirmLabel}
        cancelLabel={modal?.cancelLabel}
        onConfirm={modal?.onConfirm || (() => setModal(null))}
        onClose={() => setModal(null)}
      />
    </div>
  );
}

// Form compatto per creare una vetrina con il fornitore gia' fissato.
function CreaSchedaPerFornitore({ providerId, onCreata }) {
  const [form, setForm] = useState({ tipo: 'location', nome: '', descrizione: '', prezzo_min: '', prezzo_max: '', categoria: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setMsg(null); setSaving(true);
    try {
      if (!form.nome.trim()) throw new Error('Il nome è obbligatorio');
      await api.post('/admin/schede', {
        provider_id: providerId,
        ...form,
        prezzo_min: form.prezzo_min || null,
        prezzo_max: form.prezzo_max || null,
        categoria: form.categoria || null,
      });
      setMsg('Vetrina creata');
      if (onCreata) setTimeout(onCreata, 600);
    } catch (e2) {
      setErr(e2.response?.data?.errore || e2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="servizio-form crea-inline">
      {err && <div className="error-message"><AlertCircle size={16} /> {err}</div>}
      {msg && <div className="status-message success">{msg}</div>}
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="tipo">Tipo *</label>
          <select id="tipo" name="tipo" value={form.tipo} onChange={handleChange}>
            {TIPI.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="categoria">Sottocategoria</label>
          <input id="categoria" name="categoria" value={form.categoria} onChange={handleChange} />
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="nome">Nome *</label>
        <input id="nome" name="nome" value={form.nome} onChange={handleChange} required />
      </div>
      <div className="form-group">
        <label htmlFor="descrizione">Descrizione (no link/email)</label>
        <textarea id="descrizione" name="descrizione" rows="3" value={form.descrizione} onChange={handleChange} />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="prezzo_min">Prezzo min (€)</label>
          <input type="number" id="prezzo_min" name="prezzo_min" min="0" value={form.prezzo_min} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="prezzo_max">Prezzo max (€)</label>
          <input type="number" id="prezzo_max" name="prezzo_max" min="0" value={form.prezzo_max} onChange={handleChange} />
        </div>
      </div>
      <button type="submit" className="btn btn-primary" disabled={saving}>
        {saving ? 'Creazione...' : 'Crea vetrina'}
      </button>
    </form>
  );
}

export default FornitoreDettaglio;
